import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, addDays, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { sv } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSubscriptionOccurrences } from "@/lib/subscriptionOccurrences";

const DAY_NAMES = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"];

type PeriodType = "full" | "this_month" | "next_month" | "custom";

interface Props {
  subscription: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  courtName: string;
  customerName: string;
  onCreated: () => void;
}

export function CreateSubscriptionInvoiceDialog({
  subscription,
  open,
  onOpenChange,
  organizationId,
  courtName,
  customerName,
  onCreated,
}: Props) {
  const { t } = useTranslation("admin");
  const [periodType, setPeriodType] = useState<PeriodType>("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  // Fetch billing config for VAT and payment terms
  const { data: billingConfig } = useQuery({
    queryKey: ["billing-config", organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("billing_config")
        .select("default_vat_rate, default_payment_terms_days")
        .eq("organization_id", organizationId)
        .single();
      return data ?? { default_vat_rate: 25, default_payment_terms_days: 30 };
    },
    enabled: !!organizationId,
  });

  const vatRate = billingConfig?.default_vat_rate ?? 25;
  const paymentTermsDays = billingConfig?.default_payment_terms_days ?? 30;

  // Calculate period dates
  const { periodFrom, periodTo } = useMemo(() => {
    const today = new Date();
    switch (periodType) {
      case "full":
        return { periodFrom: subscription.valid_from, periodTo: subscription.valid_until };
      case "this_month": {
        const from = format(startOfMonth(today), "yyyy-MM-dd");
        const to = format(endOfMonth(today), "yyyy-MM-dd");
        return { periodFrom: from > subscription.valid_from ? from : subscription.valid_from, periodTo: to < subscription.valid_until ? to : subscription.valid_until };
      }
      case "next_month": {
        const next = addMonths(today, 1);
        const from = format(startOfMonth(next), "yyyy-MM-dd");
        const to = format(endOfMonth(next), "yyyy-MM-dd");
        return { periodFrom: from > subscription.valid_from ? from : subscription.valid_from, periodTo: to < subscription.valid_until ? to : subscription.valid_until };
      }
      case "custom":
        return { periodFrom: customFrom || subscription.valid_from, periodTo: customTo || subscription.valid_until };
      default:
        return { periodFrom: subscription.valid_from, periodTo: subscription.valid_until };
    }
  }, [periodType, customFrom, customTo, subscription.valid_from, subscription.valid_until]);

  // Calculate occurrences
  const occurrences = useMemo(
    () => getSubscriptionOccurrences(subscription, periodFrom, periodTo),
    [subscription, periodFrom, periodTo],
  );

  const price = Number(subscription.price) || 0;
  const subtotal = occurrences.length * price;
  const vatAmount = Math.round(subtotal * vatRate / 100);
  const total = subtotal + vatAmount;
  const startTime = subscription.start_time?.slice(0, 5) ?? "";
  const endTime = subscription.end_time?.slice(0, 5) ?? "";
  const dueDate = format(addDays(new Date(), paymentTermsDays), "yyyy-MM-dd");
  const [editDueDate, setEditDueDate] = useState(dueDate);

  const handleCreate = async () => {
    if (occurrences.length === 0) {
      toast.error(t("invoice.noOccurrences"));
      return;
    }
    setCreating(true);
    try {
      // Get next invoice number
      const year = new Date().getFullYear();
      const { data: seq, error: seqErr } = await supabase
        .from("invoice_sequences" as any)
        .select("next_number")
        .eq("organization_id", organizationId)
        .eq("year", year)
        .single();

      let nextNumber = 1;
      if (seq) {
        nextNumber = seq.next_number;
      } else if (seqErr) {
        // Create sequence row if it doesn't exist
        await supabase.from("invoice_sequences" as any).insert({
          organization_id: organizationId,
          year,
          next_number: 2,
        });
      }

      const invoiceNumber = `F-${String(nextNumber).padStart(4, "0")}`;
      const dayName = DAY_NAMES[subscription.day_of_week ?? 0];

      // Create invoice
      const { data: invoice, error: invErr } = await supabase
        .from("invoices" as any)
        .insert({
          organization_id: organizationId,
          subscription_id: subscription.id,
          customer_id: subscription.customer_id || null,
          invoice_number: invoiceNumber,
          issue_date: format(new Date(), "yyyy-MM-dd"),
          due_date: editDueDate,
          status: "draft",
          subtotal,
          vat_rate: vatRate,
          vat_amount: vatAmount,
          total,
          notes: notes || null,
          reference: `Abonnemang ${courtName}`,
        })
        .select("id")
        .single();

      if (invErr || !invoice) throw invErr || new Error("Faktura kunde inte skapas");

      // Create invoice lines (one per occurrence)
      const lines = occurrences.map((occ, i) => ({
        invoice_id: invoice.id,
        description: `${courtName}, ${dayName} ${occ.date} ${startTime}–${endTime}`,
        quantity: 1,
        unit_price: price,
        total: price,
        vat_rate: vatRate,
        occurrence_date: occ.date,
        reference_type: "subscription",
        reference_id: subscription.id,
        sort_order: i,
      }));

      const { error: linesErr } = await supabase.from("invoice_lines" as any).insert(lines);
      if (linesErr) throw linesErr;

      // Increment invoice sequence
      if (seq) {
        await supabase
          .from("invoice_sequences" as any)
          .update({ next_number: nextNumber + 1 })
          .eq("organization_id", organizationId)
          .eq("year", year);
      }

      toast.success(t("invoice.created", { number: invoiceNumber, count: occurrences.length, total }));
      onCreated();
    } catch (err: unknown) {
      toast.error(t("invoice.createError") + ": " + (err instanceof Error ? err.message : String(err)));
    }
    setCreating(false);
  };

  const periodOptions: { value: PeriodType; label: string }[] = [
    { value: "this_month", label: `${t("invoice.thisMonth")} (${format(new Date(), "MMMM", { locale: sv })})` },
    { value: "next_month", label: `${t("invoice.nextMonth")} (${format(addMonths(new Date(), 1), "MMMM", { locale: sv })})` },
    { value: "full", label: t("invoice.fullPeriod") },
    { value: "custom", label: t("invoice.custom") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("invoice.createTitle")} — {customerName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Period selector */}
          <div className="space-y-1.5">
            <Label className="text-xs">{t("invoice.period")}</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {periodOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPeriodType(opt.value)}
                  className={cn(
                    "px-3 py-2 rounded-md border text-xs text-left transition-colors",
                    periodType === opt.value
                      ? "border-primary bg-primary/5 font-medium"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {periodType === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("invoice.from")}</Label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">{t("invoice.to")}</Label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 text-sm" />
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="rounded-lg border p-3 space-y-2">
            <p className="text-sm font-medium">
              {occurrences.length} {t("invoice.occurrences")} × {price} kr = {subtotal} kr
            </p>
            {occurrences.length > 0 && (
              <div className="max-h-32 overflow-y-auto">
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {occurrences.map((occ) => (
                    <p key={occ.date}>
                      {format(new Date(occ.date), "EEE d MMM", { locale: sv })} · {startTime}–{endTime}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div className="border-t pt-2 text-sm space-y-0.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("invoice.net")}</span>
                <span>{subtotal} kr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("invoice.vat")} ({vatRate}%)</span>
                <span>{vatAmount} kr</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>{t("invoice.total")}</span>
                <span>{total} kr</span>
              </div>
            </div>
          </div>

          {/* Due date */}
          <div>
            <Label className="text-xs">{t("invoice.dueDate")}</Label>
            <Input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} className="h-9 text-sm" />
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs">{t("invoice.notes")}</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("invoice.notesPlaceholder")} className="h-9 text-sm" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("invoice.cancel")}</Button>
          <Button onClick={handleCreate} disabled={creating || occurrences.length === 0}>
            {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t("invoice.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
